import { cn } from "./classNames-DcGnTh2N.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { X } from "./icons-ZIeZXXS7.js";
import { Button } from "./Button-D2JqBAq7.js";
import { ModalDescription, ModalOverlay, ModalTitle } from "./ModalTitle-CTECVgL8.js";
import { Separator } from "./internal/components/Separator.js";
import { useEffect, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Close, Content, Portal, Root, Trigger } from "@radix-ui/react-dialog";
//#region src/components/Modal/ModalBody.tsx
/**
* Scrollable body slot for `Modal`. Place form fields, content blocks, or detail panels here.
* Takes available height inside `Modal.Content` and scrolls when content overflows.
*/
const ModalBody = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	"data-slot": "modal-body",
	className: cn("tw:flex-1", "tw:overflow-y-auto", "tw:group-data-[argo=true]/modal:scroll-fade-y", "tw:group-data-[argo=false]/modal:px-7", "tw:group-data-[argo=true]/modal:px-4", "tw:py-1", "tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4", "tw:[&_svg]:shrink-0", className),
	...props
});
//#endregion
//#region src/components/Modal/ModalContent.tsx
const ModalContent = ({ size = "medium", align = "center", className, children, onEscapeKeyDown, ...props }) => {
	const renderArgo = useArgoDesign();
	const handleEscapeKeyDown = (event) => {
		event.stopPropagation();
		onEscapeKeyDown?.(event);
	};
	return /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(ModalOverlay, { children: /* @__PURE__ */ jsx(Content, {
		...props,
		"data-argo": renderArgo,
		"data-size": size,
		"data-align": align,
		className: cn("tailwindcss-preflight", "tw:group/modal", "tw:fixed", "tw:left-1/2", "tw:-translate-x-1/2", "tw:z-[1051]", "tw:data-[argo=false]:top-20", "tw:data-[argo=true]:top-1/2", "tw:data-[argo=true]:-translate-y-1/2", "tw:data-[argo=true]:data-[align=top]:top-20", "tw:data-[argo=true]:data-[align=top]:translate-y-0", "tw:w-full", "tw:max-w-2xl", "tw:max-h-[calc(100vh-2*theme(spacing.20))]", "tw:flex", "tw:flex-col", "tw:gap-4", "tw:data-[argo=false]:rounded-lg", "tw:data-[argo=true]:rounded-modal", "tw:bg-card", "tw:text-card-foreground", "tw:data-[argo=false]:py-8", "tw:data-[argo=true]:py-4", "tw:duration-200", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[argo=false]:data-[state=open]:zoom-in-80", "tw:data-[argo=false]:data-[state=open]:slide-in-from-top-[48%]", "tw:data-[argo=false]:data-[state=closed]:zoom-out-80", "tw:data-[argo=false]:data-[state=closed]:slide-out-to-top-[48%]", "tw:data-[argo=true]:data-[state=open]:zoom-in-95", "tw:data-[argo=true]:data-[state=closed]:zoom-out-95", {
			"tw:max-w-[480px]": size === "small",
			"tw:max-w-4xl": size === "large"
		}, className),
		onEscapeKeyDown: handleEscapeKeyDown,
		children
	}) }) });
};
//#endregion
//#region src/components/Modal/ModalFooter.tsx
/**
* The modal's action row.
*
* - A child that should fill the row carries `tw:w-full` itself; the footer is already full width.
* - tailwind-merge replaces only within a group: `tw:flex-col` keeps `items-center`, so add `tw:items-stretch`.
*/
const ModalFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	className: cn("tw:flex", "tw:items-center", "tw:justify-end", "tw:gap-2", "tw:group-data-[argo=false]/modal:px-7", "tw:group-data-[argo=true]/modal:px-4", className)
});
//#endregion
//#region src/components/Modal/ModalActions.tsx
/**
* Container for the header's top-right controls — any `actions` passed to `Modal.Header` plus the
* close icon. Sits in the header grid's last column, top-aligned. Mirrors `Alert`'s action slot.
*/
const ModalActions = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:col-start-3", "tw:row-start-1", "tw:self-start", "tw:flex", "tw:items-center", "tw:gap-1", "tw:group-data-[argo=true]/modal:ml-3", className),
	...props
});
//#endregion
//#region src/components/Modal/ModalHeader.tsx
/** Modal header: `[icon] | title (children) | actions + close` on row 1, `description` on row 2. */
const ModalHeader = ({ className, icon, description, "aria-description": ariaDescription, actions, hideCloseIcon = false, children, ...props }) => /* @__PURE__ */ jsxs("div", {
	...props,
	"data-slot": "modal-header",
	className: cn("tw:grid", "tw:grid-cols-[auto_1fr_auto]", "tw:items-center", "tw:group-data-[argo=false]/modal:px-7", "tw:group-data-[argo=true]/modal:px-4", className),
	children: [
		icon,
		children && /* @__PURE__ */ jsx(ModalTitle, {
			className: "tw:col-start-2 tw:row-start-1",
			children
		}),
		(actions || !hideCloseIcon) && /* @__PURE__ */ jsxs(ModalActions, { children: [actions, !hideCloseIcon && /* @__PURE__ */ jsx(Close, {
			asChild: true,
			children: /* @__PURE__ */ jsxs(Button, {
				variant: "invisible",
				size: "icon-sm",
				children: [/* @__PURE__ */ jsx(X, {}), /* @__PURE__ */ jsx("span", {
					className: "tw:sr-only",
					children: "Close"
				})]
			})
		})] }),
		(description || ariaDescription) && /* @__PURE__ */ jsx(ModalDescription, {
			className: cn("tw:col-start-1", "tw:col-end-4", "tw:row-start-2", description ? "tw:mt-1" : "tw:sr-only"),
			children: description ?? ariaDescription
		}),
		/* @__PURE__ */ jsx("div", {
			className: cn("tw:col-start-1", "tw:col-end-4", "tw:mt-5", "tw:hidden", "tw:group-data-[argo=false]/modal:[[data-slot=modal-header]:has(~[data-slot=modal-body])_&]:block", "tw:group-data-[argo=false]/modal:[[data-slot=modal-header]:has(~[data-slot=modal-body]_[data-slot=tabs-root])_&]:!hidden"),
			children: /* @__PURE__ */ jsx(Separator, { orientation: "horizontal" })
		})
	]
});
//#endregion
//#region src/components/Modal/ModalIcon.tsx
const NAMED_COLORS = /* @__PURE__ */ new Set([
	"orange",
	"blue",
	"red",
	"green",
	"gray",
	"violet"
]);
/**
* Status-coloured icon for `Modal`, authored inside `Modal.Header`; remote images use `Modal.Image`.
*
* - Legacy: solid circular badge, absolutely docked to `Modal.Content`'s top-left.
* - Argo (`data-argo` on `Modal.Content`): soft-tinted inline badge next to the title.
* - CSS (`group-data-[argo=true]/modal:`) toggles the two, no context.
* - `color` is categorical (`red` destructive … `violet` agent), fixed across brand themes.
*/
const ModalIcon = ({ color, icon }) => {
	const customColor = color && !NAMED_COLORS.has(color) ? color : void 0;
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "modal-icon",
		className: cn("tw:flex", "tw:shrink-0", "tw:items-center", "tw:justify-center", "tw:[&_svg]:aspect-square", "tw:group-data-[argo=false]/modal:absolute", "tw:group-data-[argo=false]/modal:top-0", "tw:group-data-[argo=false]/modal:left-8", "tw:group-data-[argo=false]/modal:-translate-y-1/2", "tw:group-data-[argo=false]/modal:size-12", "tw:group-data-[argo=false]/modal:rounded-full", "tw:group-data-[argo=false]/modal:[&_svg:not([class*=\"tw:size-\"])]:size-6", "tw:group-data-[argo=true]/modal:col-start-1", "tw:group-data-[argo=true]/modal:row-start-1", "tw:group-data-[argo=true]/modal:self-start", "tw:group-data-[argo=true]/modal:mr-3", "tw:group-data-[argo=true]/modal:size-8", "tw:group-data-[argo=true]/modal:rounded", "tw:group-data-[argo=true]/modal:overflow-hidden", "tw:group-data-[argo=true]/modal:[&_svg:not([class*=\"tw:size-\"])]:size-5", {
			"tw:group-data-[argo=false]/modal:bg-warning-500": color === "orange",
			"tw:group-data-[argo=false]/modal:bg-blue-500": color === "blue",
			"tw:group-data-[argo=false]/modal:bg-green-500": color === "green",
			"tw:group-data-[argo=false]/modal:bg-error-500": color === "red",
			"tw:group-data-[argo=false]/modal:bg-neutral-150": color === "gray",
			"tw:group-data-[argo=false]/modal:bg-purple-100": color === "violet",
			"tw:group-data-[argo=false]/modal:text-white": color !== "violet",
			"tw:group-data-[argo=false]/modal:text-purple-500": color === "violet"
		}, {
			"tw:group-data-[argo=true]/modal:bg-warning-100 tw:group-data-[argo=true]/modal:text-warning-500": color === "orange",
			"tw:group-data-[argo=true]/modal:bg-blue-100 tw:group-data-[argo=true]/modal:text-blue-600": color === "blue",
			"tw:group-data-[argo=true]/modal:bg-green-100 tw:group-data-[argo=true]/modal:text-green-900": color === "green",
			"tw:group-data-[argo=true]/modal:bg-error-100 tw:group-data-[argo=true]/modal:text-error-500": color === "red",
			"tw:group-data-[argo=true]/modal:bg-neutral-100 tw:group-data-[argo=true]/modal:text-neutral-500": color === "gray",
			"tw:group-data-[argo=true]/modal:bg-purple-100 tw:group-data-[argo=true]/modal:text-purple-500": color === "violet"
		}, { "tw:text-white": !!customColor }),
		style: customColor ? { backgroundColor: customColor } : void 0,
		children: icon
	});
};
//#endregion
//#region src/components/Modal/ModalImage.tsx
const ModalImage = ({ src, alt = "" }) => {
	const [hasImageError, setHasImageError] = useState(false);
	useEffect(() => setHasImageError(false), [src]);
	if (hasImageError) return null;
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "modal-image",
		className: cn("tw:shrink-0", "tw:overflow-hidden", "tw:group-data-[argo=false]/modal:absolute", "tw:group-data-[argo=false]/modal:top-0", "tw:group-data-[argo=false]/modal:left-8", "tw:group-data-[argo=false]/modal:-translate-y-1/2", "tw:group-data-[argo=false]/modal:size-12", "tw:group-data-[argo=false]/modal:rounded-full", "tw:group-data-[argo=true]/modal:flex", "tw:group-data-[argo=true]/modal:col-start-1", "tw:group-data-[argo=true]/modal:row-start-1", "tw:group-data-[argo=true]/modal:self-start", "tw:group-data-[argo=true]/modal:mr-3", "tw:group-data-[argo=true]/modal:items-center", "tw:group-data-[argo=true]/modal:justify-center", "tw:group-data-[argo=true]/modal:rounded", "tw:group-data-[argo=true]/modal:size-8"),
		children: /* @__PURE__ */ jsx("img", {
			src,
			alt,
			className: "tw:size-full tw:object-contain",
			onError: () => setHasImageError(true)
		})
	});
};
//#endregion
//#region src/components/Modal/Modal.tsx
/**
* Blocking dialog the user must act on before continuing. Built on Radix Dialog.
*
* - Not for side editing (`Sheet`), anchored confirms (`Popconfirm`) or hover previews (`HoverCard`).
* - `Modal.Content` wraps Header + Body + Footer; a form's `FormBody` goes in Body, its buttons in Footer.
*
* @see RECIPES.md#modal-form
* @see RECIPES.md#confirm-dialog
*/
const Modal = (props) => /* @__PURE__ */ jsx(Root, { ...props });
Modal.Close = Close;
Modal.Trigger = Trigger;
Modal.Content = ModalContent;
Modal.Body = ModalBody;
Modal.Icon = ModalIcon;
Modal.Image = ModalImage;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;
Modal.Overlay = ModalOverlay;
//#endregion
export { Modal };

//# sourceMappingURL=Modal-DMZRswk4.js.map